import React, { useState, useEffect, useCallback } from 'react';
import { authAPI, eventsAPI, uploadAPI } from '../services/api';
import { User } from '../types';
import './Admin.css';

interface Event {
  id: number;
  title: string;
  date: string;
  time: string;
  description: string;
  image?: string;
  imageUrl?: string;
  images?: string[];
  isRecurring?: boolean;
  recurringType?: 'NONE' | 'WEEKLY' | 'MONTHLY' | 'YEARLY';
  recurringDay?: number; // Day of week (0-6) for weekly
  recurringWeek?: number; // Week of month (1-4, 5 for last) for monthly by week+day
  recurringDate?: number; // Day of month (1-31) for monthly by date
  recurringEndDate?: string; // When to stop recurring
  isPaused?: boolean; // Whether recurring generation is paused
}

// Helper function to format date for HTML date input (YYYY-MM-DD) - timezone aware
const formatDateForInput = (dateValue: string | Date): string => {
  if (!dateValue) return '';
  const date = new Date(dateValue);
  if (isNaN(date.getTime())) return '';

  // Use local timezone instead of UTC to avoid date shifting
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

const Admin: React.FC = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  // Event management state
  const [events, setEvents] = useState<Event[]>([]);
  const [editingEvent, setEditingEvent] = useState<Event | null>(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [selectedImageFiles, setSelectedImageFiles] = useState<File[]>([]);
  const [removedImageUrls, setRemovedImageUrls] = useState<string[]>([]);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [eventsPerPage] = useState(10); // Show 10 events per page

  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  // const [activeTab, setActiveTab] = useState<'reservations' | 'events'>('reservations');

  const fetchEvents = useCallback(async () => {
    try {
      const response = await eventsAPI.getAllAdmin();
      const allEvents = response.data || [];
      // Sort events by date (newest first) for better admin experience
      const sortedEvents = allEvents.sort((a: Event, b: Event) => new Date(b.date).getTime() - new Date(a.date).getTime());
      setEvents(sortedEvents);
      setCurrentPage(1); // Reset to first page when data changes
    } catch (error: any) {
      console.error('Error fetching events:', error);
      if (error.code === 'ERR_NETWORK') {
        setError('Cannot connect to server. Please make sure the backend is running.');
      } else {
        setError('Failed to load events');
      }
    }
  }, []);

  // Search and pagination calculations
  const filteredEvents = events.filter((event: Event) =>
    event.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const indexOfLastEvent = currentPage * eventsPerPage;
  const indexOfFirstEvent = indexOfLastEvent - eventsPerPage;
  const currentEvents = filteredEvents.slice(indexOfFirstEvent, indexOfLastEvent);
  const totalPages = Math.ceil(filteredEvents.length / eventsPerPage);

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  const checkAuthStatus = useCallback(async () => {
    try {
      const response = await authAPI.getProfile();
      setUser(response.data);
      setIsLoggedIn(true);
      fetchEvents();
    } catch (error) {
      localStorage.removeItem('authToken');
      setIsLoggedIn(false);
    }
  }, [fetchEvents]);

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (token) {
      checkAuthStatus();
    }
  }, [checkAuthStatus]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      const response = await authAPI.login(credentials);
      localStorage.setItem('authToken', response.data.token);
      setUser(response.data.user);
      setIsLoggedIn(true);
      setCredentials({ username: '', password: '' });
    } catch (error: any) {
      if (error.code === 'ERR_NETWORK') {
        setError('Cannot connect to server. Please make sure the backend is running.');
      } else {
        setError(error.response?.data?.message || 'Login failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    setIsLoggedIn(false);
    setUser(null);
    setCredentials({ username: '', password: '' });
  };

  /*
  // Commented out reservation logic
  const fetchDashboardData = useCallback(async () => {
    try {
      const [statsResponse, reservationsResponse] = await Promise.all([
        reservationsAPI.getStats(),
        reservationsAPI.getAll({ limit: 10 })
      ]);
      setStats(statsResponse.data);
      setReservations(reservationsResponse.data || []);
    } catch (error: any) {
      console.error('Error fetching dashboard data:', error);
      if (error.code === 'ERR_NETWORK') {
        setError('Cannot connect to server. Please make sure the backend is running.');
      } else {
        setError('Failed to load dashboard data');
      }
    }
  }, []);

  const checkAuthStatus = useCallback(async () => {
    try {
      const response = await authAPI.getProfile();
      setUser(response.data);
      setIsLoggedIn(true);
      fetchDashboardData();
    } catch (error) {
      localStorage.removeItem('authToken');
      setIsLoggedIn(false);
    }
  }, [fetchDashboardData]);

  useEffect(() => {
    // Check if user is already logged in
    const token = localStorage.getItem('authToken');
    if (token) {
      checkAuthStatus();
    }
  }, [checkAuthStatus]);
  */

  // Event management functions
  const handleEditEvent = (event: Event) => {
    setEditingEvent({
      ...event,
      date: formatDateForInput(event.date), // Format date for HTML date input
      // Ensure recurring fields are properly initialized
      isRecurring: event.isRecurring || false,
      recurringType: event.recurringType || 'NONE',
      recurringDay: event.recurringDay,
      recurringWeek: event.recurringWeek,
      recurringDate: event.recurringDate,
      recurringEndDate: event.recurringEndDate || ''
    });
    setShowEventModal(true);
    setError(null);
    setSuccessMessage(null);
  };

  const handleNewEvent = () => {
    setEditingEvent({
      id: 0,
      title: '',
      date: '',
      time: '',
      description: '',
      image: '',
      // Initialize recurring fields for new events
      isRecurring: false,
      recurringType: 'NONE',
      recurringDay: undefined,
      recurringWeek: undefined,
      recurringEndDate: ''
    });
    setSelectedImageFiles([]);
    setShowEventModal(true);
    setError(null);
    setSuccessMessage(null);
  };

  const handleEventSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingEvent) return;

    setSubmitting(true);
    setError(null);
    setSuccessMessage(null);

    try {
      let imageUrls: string[] = editingEvent.images || [];

      // Clean up removed images from Firebase storage
      if (removedImageUrls.length > 0) {
        try {
          for (const imageUrl of removedImageUrls) {
            await uploadAPI.deleteImage(imageUrl);
          }
        } catch (cleanupError) {
          console.warn('Some images could not be deleted from storage:', cleanupError);
        }
      }

      // Upload new images and add to existing ones
      if (selectedImageFiles.length > 0) {
        const uploadResponse = await uploadAPI.uploadImages(selectedImageFiles, 'events');
        const newImageUrls = uploadResponse.data.images.map((img: any) => img.imageUrl);
        imageUrls = [...imageUrls, ...newImageUrls]; // Add to existing images
      }

      // Prepare event data with images array and recurring fields
      const eventData = {
        title: editingEvent.title,
        date: editingEvent.date,
        time: editingEvent.time,
        description: editingEvent.description,
        images: imageUrls,
        // Keep single image for backward compatibility
        image: imageUrls.length > 0 ? imageUrls[0] : null,
        // Include recurring fields
        isRecurring: editingEvent.isRecurring || false,
        recurringType: editingEvent.recurringType || 'NONE',
        recurringDay: editingEvent.recurringDay || null,
        recurringWeek: editingEvent.recurringWeek || null,
        recurringDate: editingEvent.recurringDate || null,
        recurringEndDate: editingEvent.recurringEndDate || null
      };


      if (editingEvent.id === 0) {
        // Create new event
        await eventsAPI.create(eventData);
        setSuccessMessage('Event created successfully!');
      } else {
        // Update existing event
        await eventsAPI.update(editingEvent.id.toString(), eventData);
        setSuccessMessage('Event updated successfully!');
      }

      // Refresh events list
      fetchEvents();

      setTimeout(() => {
        setShowEventModal(false);
        setEditingEvent(null);
        setSelectedImageFiles([]);
        setRemovedImageUrls([]);
        setSuccessMessage(null);
      }, 2000);
    } catch (error: any) {
      if (error.code === 'ERR_NETWORK') {
        setError('Cannot connect to server. Please make sure the backend is running.');
      } else {
        setError(error.response?.data?.message || 'Failed to save event');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteEvent = async (eventId: number) => {
    if (window.confirm('Are you sure you want to delete this event?')) {
      try {
        await eventsAPI.delete(eventId.toString());
        fetchEvents(); // Refresh events list
        setSuccessMessage('Event deleted successfully!');
        setTimeout(() => setSuccessMessage(null), 3000);
      } catch (error: any) {
        if (error.code === 'ERR_NETWORK') {
          setError('Cannot connect to server. Please make sure the backend is running.');
        } else {
          setError(error.response?.data?.message || 'Failed to delete event');
        }
        setTimeout(() => setError(null), 5000);
      }
    }
  };

  const handlePauseEvent = async (eventId: number) => {
    if (window.confirm('Are you sure you want to pause this recurring event? This will remove all future occurrences.')) {
      try {
        await eventsAPI.pause(eventId.toString());
        fetchEvents(); // Refresh events list
        setSuccessMessage('Event paused successfully!');
        setTimeout(() => setSuccessMessage(null), 3000);
      } catch (error: any) {
        if (error.code === 'ERR_NETWORK') {
          setError('Cannot connect to server. Please make sure the backend is running.');
        } else {
          setError(error.response?.data?.message || 'Failed to pause event');
        }
        setTimeout(() => setError(null), 5000);
      }
    }
  };

  const handleResumeEvent = async (eventId: number) => {
    if (window.confirm('Are you sure you want to resume this recurring event? Future occurrences will be calculated when viewing months.')) {
      try {
        await eventsAPI.resume(eventId.toString());
        fetchEvents(); // Refresh events list
        setSuccessMessage('Event resumed successfully!');
        setTimeout(() => setSuccessMessage(null), 3000);
      } catch (error: any) {
        if (error.code === 'ERR_NETWORK') {
          setError('Cannot connect to server. Please make sure the backend is running.');
        } else {
          setError(error.response?.data?.message || 'Failed to resume event');
        }
        setTimeout(() => setError(null), 5000);
      }
    }
  };

  const handleEventModalClose = () => {
    setShowEventModal(false);
    setEditingEvent(null);
    setSelectedImageFiles([]);
    setRemovedImageUrls([]);
    setError(null);
    setSuccessMessage(null);
  };

  const handleImageFilesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      setSelectedImageFiles(files);
    }
  };

  const removeImageFile = (index: number) => {
    setSelectedImageFiles(prev => prev.filter((_, i) => i !== index));
  };

  const removeExistingImage = (index: number) => {
    if (!editingEvent) return;

    const imageToRemove = editingEvent.images?.[index];
    if (imageToRemove) {
      setRemovedImageUrls(prev => [...prev, imageToRemove]);
    }

    setEditingEvent(prev => {
      if (!prev) return null;
      const updatedImages = prev.images?.filter((_, i) => i !== index) || [];
      return { ...prev, images: updatedImages };
    });
  };

  const removeSingleImage = () => {
    if (!editingEvent) return;

    const imageToRemove = editingEvent.imageUrl || editingEvent.image;
    if (imageToRemove) {
      setRemovedImageUrls(prev => [...prev, imageToRemove]);
    }

    setEditingEvent(prev => {
      if (!prev) return null;
      return {
        ...prev,
        image: undefined,
        imageUrl: undefined,
        images: []
      };
    });
  };

  if (!isLoggedIn) {
    return (
      <div className="admin-page">
        <div className="admin-login">
          <div className="login-card">
            <h1 className="login-title">Admin Login</h1>
            {error && <div className="error-message">{error}</div>}
            <form onSubmit={handleLogin} className="login-form">
              <div className="form-field">
                <label className="form-label">Username</label>
                <input
                  type="text"
                  value={credentials.username}
                  onChange={(e) => setCredentials({...credentials, username: e.target.value})}
                  className="form-input"
                  required
                  disabled={loading}
                />
              </div>
              <div className="form-field">
                <label className="form-label">Password</label>
                <input
                  type="password"
                  value={credentials.password}
                  onChange={(e) => setCredentials({...credentials, password: e.target.value})}
                  className="form-input"
                  required
                  disabled={loading}
                />
              </div>
              <button type="submit" className="btn-login" disabled={loading}>
                {loading ? 'Logging in...' : 'Login'}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="admin-dashboard">
        <div className="dashboard-header">
          <h1 className="dashboard-title">Event Management</h1>
          <div className="dashboard-user">
            <span>Welcome, {user?.username || 'Admin'}</span>
            <button
              onClick={handleLogout}
              className="btn-logout"
            >
              Logout
            </button>
          </div>
        </div>

        <div className="stats-grid">
          <div className="stat-card">
            <h3 className="stat-label">Total Events</h3>
            <p className="stat-value">{events.length}</p>
          </div>
          <div className="stat-card">
            <h3 className="stat-label">Upcoming Events</h3>
            <p className="stat-value">{events.filter(e => new Date(e.date) >= new Date()).length}</p>
          </div>
          <div className="stat-card">
            <h3 className="stat-label">Past Events</h3>
            <p className="stat-value">{events.filter(e => new Date(e.date) < new Date()).length}</p>
          </div>
        </div>

        {/* Events Section */}
          <div className="events-section">
            <div className="events-header">
              <h2 className="events-title">Event Management</h2>
              <button
                className="btn-primary"
                onClick={handleNewEvent}
              >
                Add New Event
              </button>
            </div>

            {/* Search Bar */}
            <div className="search-container" style={{
              marginBottom: '2rem',
              display: 'flex',
              gap: '1rem',
              alignItems: 'center',
              backgroundColor: '#f8f9fa',
              padding: '1.5rem',
              borderRadius: '12px',
              border: '1px solid #e9ecef'
            }}>
              <div className="search-input-wrapper" style={{
                position: 'relative',
                flex: '1',
                maxWidth: '450px'
              }}>
                <input
                  type="text"
                  placeholder="Search events by title..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  style={{
                    width: '100%',
                    padding: '0.875rem 1.25rem',
                    paddingLeft: '3rem',
                    border: '2px solid #dee2e6',
                    borderRadius: '10px',
                    fontSize: '1rem',
                    outline: 'none',
                    transition: 'all 0.3s ease',
                    backgroundColor: 'white',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                    fontFamily: 'inherit'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = 'var(--lodge-brown)';
                    e.target.style.boxShadow = '0 0 0 3px rgba(139, 69, 19, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#dee2e6';
                    e.target.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)';
                  }}
                />
                <svg
                  style={{
                    position: 'absolute',
                    left: '1rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: '18px',
                    height: '18px',
                    color: '#6c757d',
                    pointerEvents: 'none'
                  }}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>

              {searchTerm && (
                <div className="search-results-info" style={{
                  color: '#495057',
                  fontSize: '0.95rem',
                  whiteSpace: 'nowrap',
                  fontWeight: '500',
                  backgroundColor: 'white',
                  padding: '0.5rem 1rem',
                  borderRadius: '8px',
                  border: '1px solid #dee2e6'
                }}>
                  {filteredEvents.length} of {events.length} events
                </div>
              )}

              {searchTerm && (
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setCurrentPage(1);
                  }}
                  style={{
                    padding: '0.625rem 1.25rem',
                    border: '2px solid #dee2e6',
                    borderRadius: '8px',
                    backgroundColor: 'white',
                    color: '#495057',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    fontWeight: '500',
                    transition: 'all 0.3s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.backgroundColor = '#f8f9fa';
                    e.currentTarget.style.borderColor = '#adb5bd';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor = 'white';
                    e.currentTarget.style.borderColor = '#dee2e6';
                  }}
                >
                  <svg
                    width="14"
                    height="14"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                  Clear
                </button>
              )}
            </div>

            <div className="events-table-container">
              <table className="events-table">
                <thead className="table-header">
                  <tr>
                    <th>Title</th>
                    <th>Date</th>
                    <th>Time</th>
                    <th>Description</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody className="table-body">
                  {currentEvents.map((event) => (
                    <tr key={event.id}>
                      <td>
                        {event.title}
                        {event.isRecurring && (
                          <div style={{
                            display: 'inline-block',
                            marginLeft: '0.5rem',
                            padding: '2px 6px',
                            backgroundColor: event.isPaused ? '#6c757d' : 'var(--lodge-orange)',
                            color: 'white',
                            fontSize: '0.7rem',
                            borderRadius: '4px',
                            fontWeight: 'bold'
                          }}>
                            {event.isPaused ? 'PAUSED' : 'RECURRING'}
                          </div>
                        )}
                      </td>
                      <td>
                        {new Date(event.date).toLocaleDateString()}
                        {event.isRecurring && (
                          <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '2px' }}>
                            {event.recurringType === 'WEEKLY' && '• Weekly'}
                            {event.recurringType === 'MONTHLY' && '• Monthly'}
                            {event.recurringType === 'YEARLY' && '• Yearly'}
                          </div>
                        )}
                      </td>
                      <td>{event.time}</td>
                      <td style={{maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                        {event.description}
                      </td>
                      <td>
                        <div className="table-actions">
                          <button
                            className="btn-table-action"
                            onClick={() => handleEditEvent(event)}
                          >
                            Edit
                          </button>
                          {event.isRecurring && (
                            event.isPaused ? (
                              <button
                                className="btn-table-action"
                                onClick={() => handleResumeEvent(event.id)}
                                style={{
                                  backgroundColor: '#28a745',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  padding: '6px 12px',
                                  fontSize: '0.875rem',
                                  fontWeight: '500',
                                  cursor: 'pointer',
                                  transition: 'all 0.2s ease',
                                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                }}
                                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#218838'}
                                onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#28a745'}
                                title="Resume recurring event"
                              >
                                Resume
                              </button>
                            ) : (
                              <button
                                className="btn-table-action"
                                onClick={() => handlePauseEvent(event.id)}
                                style={{
                                  backgroundColor: '#fd7e14',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  padding: '6px 12px',
                                  fontSize: '0.875rem',
                                  fontWeight: '500',
                                  cursor: 'pointer',
                                  transition: 'all 0.2s ease',
                                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                }}
                                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#e8681c'}
                                onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#fd7e14'}
                                title="Pause recurring event"
                              >
                                Pause
                              </button>
                            )
                          )}
                          <button
                            className="btn-table-action btn-danger"
                            onClick={() => handleDeleteEvent(event.id)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {currentEvents.length === 0 && filteredEvents.length > 0 && (
                    <tr>
                      <td colSpan={5} style={{textAlign: 'center'}}>No events on this page</td>
                    </tr>
                  )}
                  {filteredEvents.length === 0 && events.length > 0 && (
                    <tr>
                      <td colSpan={5} style={{textAlign: 'center'}}>No events match your search</td>
                    </tr>
                  )}
                  {events.length === 0 && (
                    <tr>
                      <td colSpan={5} style={{textAlign: 'center'}}>No events found</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="pagination-controls" style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '1rem',
                padding: '1rem',
                marginTop: '1rem'
              }}>
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="btn-table-action"
                  style={{
                    opacity: currentPage === 1 ? 0.5 : 1,
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
                  }}
                >
                  Previous
                </button>

                <div className="page-info" style={{
                  display: 'flex',
                  gap: '0.5rem',
                  alignItems: 'center'
                }}>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNumber => (
                    <button
                      key={pageNumber}
                      onClick={() => handlePageChange(pageNumber)}
                      className={`btn-table-action ${currentPage === pageNumber ? 'active' : ''}`}
                      style={{
                        backgroundColor: currentPage === pageNumber ? 'var(--lodge-brown)' : 'transparent',
                        color: currentPage === pageNumber ? 'white' : 'var(--lodge-brown)',
                        border: currentPage === pageNumber ? '1px solid var(--lodge-brown)' : '1px solid #ddd',
                        minWidth: '40px'
                      }}
                    >
                      {pageNumber}
                    </button>
                  ))}
                </div>

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="btn-table-action"
                  style={{
                    opacity: currentPage === totalPages ? 0.5 : 1,
                    cursor: currentPage === totalPages ? 'not-allowed' : 'pointer'
                  }}
                >
                  Next
                </button>

                <div className="pagination-info" style={{
                  fontSize: '0.9rem',
                  color: '#666',
                  marginLeft: '1rem'
                }}>
                  Showing {indexOfFirstEvent + 1}-{Math.min(indexOfLastEvent, filteredEvents.length)} of {filteredEvents.length} events
                  {searchTerm && filteredEvents.length !== events.length && (
                    <span style={{ color: '#999', fontStyle: 'italic' }}>
                      {' '}(filtered from {events.length} total)
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
      </div>

      {/* Commented out reservation modal code */}

      {/* Event Modal */}
      {showEventModal && editingEvent && (
        <div className="modal-overlay" onClick={handleEventModalClose}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingEvent.id === 0 ? 'Add New Event' : 'Edit Event'}</h2>
              <button 
                className="modal-close"
                onClick={handleEventModalClose}
              >
                ×
              </button>
            </div>
            
            {error && <div className="error-message">{error}</div>}
            {successMessage && <div className="success-message">{successMessage}</div>}
            
            <form onSubmit={handleEventSubmit} className="edit-form">
              <div className="form-field">
                <label className="form-label">Event Title *</label>
                <input
                  type="text"
                  value={editingEvent.title}
                  onChange={(e) => setEditingEvent(prev => prev ? { ...prev, title: e.target.value } : null)}
                  className="form-input"
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-field">
                  <label className="form-label">Date *</label>
                  <input
                    type="date"
                    value={editingEvent.date}
                    onChange={(e) => setEditingEvent(prev => prev ? { ...prev, date: e.target.value } : null)}
                    className="form-input"
                    required
                  />
                </div>
                <div className="form-field">
                  <label className="form-label">Time *</label>
                  <input
                    type="text"
                    value={editingEvent.time}
                    onChange={(e) => setEditingEvent(prev => prev ? { ...prev, time: e.target.value } : null)}
                    className="form-input"
                    placeholder="e.g. 7:00 PM"
                    required
                  />
                </div>
              </div>

              <div className="form-field">
                <label className="form-label">Event Images</label>
                <div className="file-input-container">
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImageFilesChange}
                    className="file-input-hidden"
                    id="event-images-upload"
                  />
                  <label htmlFor="event-images-upload" className="file-input-button">
                    <span className="file-input-icon">📁</span>
                    Choose Images
                  </label>
                </div>


                {/* Display existing images */}
                <div style={{marginTop: '0.5rem'}}>
                  {/* Handle multiple images */}
                  {editingEvent.images && editingEvent.images.length > 0 && (
                    <>
                      <small style={{color: '#666', fontSize: '0.8rem', display: 'block', marginBottom: '0.25rem'}}>
                        Current images ({editingEvent.images.length}):
                      </small>
                      <div style={{display: 'flex', flexWrap: 'wrap', gap: '0.5rem'}}>
                        {editingEvent.images.map((imageUrl, index) => (
                          <div key={index} style={{position: 'relative', display: 'inline-block'}}>
                            <img
                              src={imageUrl}
                              alt={`Event ${index + 1}`}
                              style={{width: '80px', height: '60px', objectFit: 'cover', borderRadius: '4px'}}
                            />
                            <button
                              type="button"
                              onClick={() => removeExistingImage(index)}
                              style={{
                                position: 'absolute',
                                top: '-5px',
                                right: '-5px',
                                background: '#ff4444',
                                color: 'white',
                                border: 'none',
                                borderRadius: '50%',
                                width: '20px',
                                height: '20px',
                                fontSize: '12px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                              title="Remove image"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    </>
                  )}

                  {/* Fallback to single image if no images array */}
                  {(!editingEvent.images || editingEvent.images.length === 0) &&
                   (editingEvent.image || editingEvent.imageUrl) && (
                    <>
                      <small style={{color: '#666', fontSize: '0.8rem', display: 'block', marginBottom: '0.25rem'}}>
                        Current image:
                      </small>
                      <div style={{position: 'relative', display: 'inline-block'}}>
                        <img
                          src={editingEvent.imageUrl || editingEvent.image}
                          alt="Event preview"
                          style={{width: '80px', height: '60px', objectFit: 'cover', borderRadius: '4px'}}
                        />
                        <button
                          type="button"
                          onClick={() => removeSingleImage()}
                          style={{
                            position: 'absolute',
                            top: '-5px',
                            right: '-5px',
                            background: '#ff4444',
                            color: 'white',
                            border: 'none',
                            borderRadius: '50%',
                            width: '20px',
                            height: '20px',
                            fontSize: '12px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                          title="Remove image"
                        >
                          ×
                        </button>
                      </div>
                    </>
                  )}
                </div>

                {/* Display selected new files */}
                {selectedImageFiles.length > 0 && (
                  <div style={{marginTop: '0.5rem'}}>
                    <small style={{color: '#28a745', fontSize: '0.8rem', display: 'block', marginBottom: '0.25rem'}}>
                      ✓ New files to add ({selectedImageFiles.length}):
                    </small>
                    {selectedImageFiles.map((file, index) => (
                      <div key={index} style={{display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem'}}>
                        <span style={{fontSize: '0.8rem', flex: 1}}>{file.name}</span>
                        <button
                          type="button"
                          onClick={() => removeImageFile(index)}
                          style={{
                            background: '#ff4444',
                            color: 'white',
                            border: 'none',
                            borderRadius: '3px',
                            padding: '2px 6px',
                            fontSize: '0.7rem',
                            cursor: 'pointer'
                          }}
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <small style={{color: '#666', fontSize: '0.8rem', marginTop: '0.25rem', display: 'block'}}>
                  Choose up to 5 image files from your computer (max 5MB each)
                </small>
              </div>

              <div className="form-field">
                <label className="form-label">Description *</label>
                <textarea
                  value={editingEvent.description}
                  onChange={(e) => setEditingEvent(prev => prev ? { ...prev, description: e.target.value } : null)}
                  className="form-textarea"
                  rows={3}
                  required
                />
              </div>

              {/* Recurring Event Options */}
              <div className="form-field">
                <label className="form-label">Recurring Options</label>
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={editingEvent.isRecurring || false}
                      onChange={(e) => setEditingEvent(prev => prev ? {
                        ...prev,
                        isRecurring: e.target.checked,
                        recurringType: e.target.checked ? 'WEEKLY' : 'NONE'
                      } : null)}
                    />
                    Make this a recurring event
                  </label>
                </div>

                {editingEvent.isRecurring && (
                  <div style={{ marginLeft: '1rem', padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                    <div style={{ marginBottom: '1rem' }}>
                      <label className="form-label" style={{ fontSize: '0.9rem' }}>Repeat Pattern</label>
                      <select
                        value={editingEvent.recurringType || 'WEEKLY'}
                        onChange={(e) => setEditingEvent(prev => prev ? {
                          ...prev,
                          recurringType: e.target.value as 'WEEKLY' | 'MONTHLY' | 'YEARLY'
                        } : null)}
                        className="form-input"
                      >
                        <option value="WEEKLY">Weekly (same day each week)</option>
                        <option value="MONTHLY">Monthly</option>
                        <option value="YEARLY">Yearly (same date each year)</option>
                      </select>
                    </div>

                    {editingEvent.recurringType === 'MONTHLY' && (
                      <>
                        <div style={{ marginBottom: '1rem' }}>
                          <label className="form-label" style={{ fontSize: '0.9rem' }}>Monthly Pattern</label>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                              <input
                                type="radio"
                                name="monthlyPattern"
                                checked={!!editingEvent.recurringDate}
                                onChange={() => setEditingEvent(prev => prev ? {
                                  ...prev,
                                  recurringDate: new Date(prev.date).getDate(),
                                  recurringWeek: undefined,
                                  recurringDay: undefined
                                } : null)}
                              />
                              <span>Same date each month (e.g., 1st of every month)</span>
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                              <input
                                type="radio"
                                name="monthlyPattern"
                                checked={!!editingEvent.recurringWeek}
                                onChange={() => setEditingEvent(prev => prev ? {
                                  ...prev,
                                  recurringWeek: 1,
                                  recurringDay: new Date(prev.date).getDay(),
                                  recurringDate: undefined
                                } : null)}
                              />
                              <span>Same week and day (e.g., first Saturday)</span>
                            </label>
                          </div>
                        </div>

                        {editingEvent.recurringDate && (
                          <div style={{ marginBottom: '1rem' }}>
                            <label className="form-label" style={{ fontSize: '0.9rem' }}>Day of Month</label>
                            <select
                              value={editingEvent.recurringDate || 1}
                              onChange={(e) => setEditingEvent(prev => prev ? {
                                ...prev,
                                recurringDate: parseInt(e.target.value)
                              } : null)}
                              className="form-input"
                            >
                              {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
                                <option key={day} value={day}>{day}</option>
                              ))}
                            </select>
                            <small style={{ color: '#666', fontSize: '0.8rem' }}>
                              For months with fewer days, the last day of the month will be used
                            </small>
                          </div>
                        )}

                        {editingEvent.recurringWeek && (
                          <>
                            <div style={{ marginBottom: '1rem' }}>
                              <label className="form-label" style={{ fontSize: '0.9rem' }}>Week of Month</label>
                              <select
                                value={editingEvent.recurringWeek || 1}
                                onChange={(e) => setEditingEvent(prev => prev ? {
                                  ...prev,
                                  recurringWeek: parseInt(e.target.value)
                                } : null)}
                                className="form-input"
                              >
                                <option value={1}>First</option>
                                <option value={2}>Second</option>
                                <option value={3}>Third</option>
                                <option value={4}>Fourth</option>
                                <option value={5}>Last</option>
                              </select>
                            </div>

                            <div style={{ marginBottom: '1rem' }}>
                              <label className="form-label" style={{ fontSize: '0.9rem' }}>Day of Week</label>
                              <select
                                value={editingEvent.recurringDay !== undefined ? editingEvent.recurringDay : new Date(editingEvent.date).getDay()}
                                onChange={(e) => setEditingEvent(prev => prev ? {
                                  ...prev,
                                  recurringDay: parseInt(e.target.value)
                                } : null)}
                                className="form-input"
                              >
                                <option value={0}>Sunday</option>
                                <option value={1}>Monday</option>
                                <option value={2}>Tuesday</option>
                                <option value={3}>Wednesday</option>
                                <option value={4}>Thursday</option>
                                <option value={5}>Friday</option>
                                <option value={6}>Saturday</option>
                              </select>
                            </div>
                          </>
                        )}
                      </>
                    )}

                    <div style={{ marginBottom: '1rem' }}>
                      <label className="form-label" style={{ fontSize: '0.9rem' }}>End Date (optional)</label>
                      <input
                        type="date"
                        value={editingEvent.recurringEndDate || ''}
                        onChange={(e) => setEditingEvent(prev => prev ? {
                          ...prev,
                          recurringEndDate: e.target.value
                        } : null)}
                        className="form-input"
                      />
                      <small style={{ color: '#666', fontSize: '0.8rem' }}>
                        Leave empty for events that repeat indefinitely
                      </small>
                    </div>

                    <div style={{ fontSize: '0.85rem', color: '#666', fontStyle: 'italic' }}>
                      {editingEvent.recurringType === 'WEEKLY' && 'Repeats every week on the same day'}
                      {editingEvent.recurringType === 'MONTHLY' &&
                        `Repeats on the ${
                          editingEvent.recurringWeek === 1 ? 'first' :
                          editingEvent.recurringWeek === 2 ? 'second' :
                          editingEvent.recurringWeek === 3 ? 'third' :
                          editingEvent.recurringWeek === 4 ? 'fourth' : 'last'
                        } ${
                          ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][
                            editingEvent.recurringDay !== undefined ? editingEvent.recurringDay : new Date(editingEvent.date).getDay()
                          ]
                        } of each month`
                      }
                      {editingEvent.recurringType === 'YEARLY' && 'Repeats on the same date each year'}
                    </div>
                  </div>
                )}
              </div>

              <div className="form-actions">
                <button 
                  type="button" 
                  className="btn-secondary"
                  onClick={handleEventModalClose}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn-primary"
                  disabled={submitting}
                >
                  {submitting ? 'Saving...' : (editingEvent.id === 0 ? 'Create Event' : 'Save Changes')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default Admin;